import { useContext, useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { AccountSessionContext, formatAccountRole } from "./account-context";

export function AccountMenu({ user, placement = "standalone" }: {
  user: { name: string; role: string };
  placement?: "header" | "standalone";
}) {
  const account = useContext(AccountSessionContext);
  const roleLabel = account?.roleLabel ?? formatAccountRole(user.role);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const initialFocus = useRef<"first" | "last">("first");
  const menuId = useId();
  const initials = user.name.trim().split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase() || "?";

  function menuItems() {
    return Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);
  }

  function openMenu(position: "first" | "last" = "first") {
    initialFocus.current = position;
    setOpen(true);
  }

  function closeMenu(returnFocus = false) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    const items = menuItems();
    (initialFocus.current === "last" ? items.at(-1) : items[0])?.focus();

    function dismiss(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const items = menuItems();
    const index = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeMenu(true);
    } else if (event.key === "Tab") {
      closeMenu(true);
    } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1
        : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
      items[next]?.focus();
    }
  }

  return (
    <div className={`account-menu account-menu-${placement}`} ref={rootRef} onBlur={(event) => {
      if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) closeMenu();
    }}>
      <button
        className="account-trigger"
        ref={triggerRef}
        type="button"
        aria-label="Open account menu"
        title={user.name}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={!account}
        onClick={() => open ? closeMenu() : openMenu()}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            openMenu(event.key === "ArrowUp" ? "last" : "first");
          } else if (event.key === "Escape" && open) {
            event.preventDefault();
            closeMenu(true);
          }
        }}
      >
        <span className="avatar" aria-hidden="true">{initials}</span>
        {placement === "standalone" ? <span className="account-summary"><strong title={user.name}>{user.name}</strong><small>{roleLabel}</small></span> : null}
        <svg className="account-chevron" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d={placement === "header" ? "m6 8 4 4 4-4" : "m6 12 4-4 4 4"} /></svg>
      </button>
      {open && account ? (
        <div className="account-popover" onKeyDown={handleMenuKeyDown}>
          <div className="account-details">
            <div className="account-identity"><strong>{user.name}</strong><small>{roleLabel}</small></div>
            <span className="account-email" title={account.email}>{account.email}</span>
          </div>
          <div id={menuId} ref={menuRef} role="menu" aria-label="Account menu">
            {account.profileHref ? <a className="account-menu-item" role="menuitem" tabIndex={-1} href={account.profileHref}>
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="6" r="3" /><path d="M4 17v-2a6 6 0 0 1 12 0v2" /></svg>
              My profile
            </a> : null}
            <a className="account-menu-item" role="menuitem" tabIndex={-1} href={account.changePasswordHref}>
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="4" y="8" width="12" height="9" rx="2" /><path d="M6.5 8V5a3.5 3.5 0 0 1 7 0v3M10 11v3" /></svg>
              Change password
            </a>
            <button className="account-menu-item account-sign-out" role="menuitem" tabIndex={-1} type="button" disabled={account.busy} onClick={() => void account.signOut()}>
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M8 3H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h4M8 10h9m-3-3 3 3-3 3" /></svg>
              {account.busy ? "Signing out…" : "Sign out"}
            </button>
          </div>
          {account.error ? <p role="alert" className="account-error">{account.error}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
